import { can } from "../../auth/can.ts";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import moment from "moment";
import "moment/locale/es";
import { getCurrencyFormat } from "../../utils/index.ts";
import { useNavigate } from "react-router-dom";
import EmptyImage from "../../assets/img/ic_content.svg";
import { deleteApplication } from "../../services/appoinment.service.ts";
import {
  setCurrentUser,
  setDefaultSort,
  setFetch,
  setModalBan,
  setModalSetStatus,
  setPage,
  setShowModal,
  setSortBy,
} from "../../store/slices/solicitudesSlice.ts";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { deletePartner } from "../../services/partner.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import ModalContainer from "../ModalContainer.tsx";
import DeleteSolicitudes from "./DeleteSolicitudes.tsx";
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";

const STATUS_COLORS: Record<string, { main: string; light: string; border: string; icon: string }> = {
  Solicitado:           { main: "#1d4ed8", light: "#eff6ff", border: "#bfdbfe", icon: "fa-user" },
  Entrevistado:         { main: "#7a6200", light: "#fffbeb", border: "#F5D76E", icon: "fa-comments" },
  Aceptado:             { main: "#15803d", light: "#f0fdf4", border: "#bbf7d0", icon: "fa-check" },
  "Entrevista con Lider":{ main: "#0e7490", light: "#ecfeff", border: "#a5f3fc", icon: "fa-user-tie" },
  "Pre-Colaborador":    { main: "#c2410c", light: "#fff7ed", border: "#fed7aa", icon: "fa-id-badge" },
  Rechazado:            { main: "#b91c1c", light: "#fef2f2", border: "#fecaca", icon: "fa-times" },
  Cartera:              { main: "#4b5563", light: "#f9fafb", border: "#e5e7eb", icon: "fa-archive" },
};

const getStatusBadgeStyle = (statusName: string): React.CSSProperties => {
  const colors = STATUS_COLORS[statusName] || { main: "#4b5563", light: "#f9fafb", border: "#e5e7eb", icon: "" };
  return {
    backgroundColor: colors.light,
    color: colors.main,
    border: `1px solid ${colors.border}`,
    padding: "0.25rem 0.6rem",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: "0.3rem",
    whiteSpace: "nowrap",
  };
};

const getStatusIcon = (statusName: string): string => {
  return STATUS_COLORS[statusName]?.icon || "fa-circle";
};

moment.locale("es");

const TableSolicitudes = ({ refetch }) => {
  const dispatch = useDispatch();
  const { fetch, sortBy, defaultSort } = useSelector(
    (state: RootState) => state.solicitud
  );

  const navigate = useNavigate();
  const { solicitudesList } = useSelector(
    (state: RootState) => state.solicitud
  );
  const { user } = useSelector((state: RootState) => state.auth);

  function handleClick(application, disabledForm = false) {
    if (
      application.applicationStatus.name == "Solicitado" ||
      application.applicationStatus.name == "Entrevistado"
    ) {
      navigate("/result/form", { state: { ...application, disabledForm } });
    } else {
      if (user.branch) {
        navigate("aprobar/" + application.id);
      } else {
        navigate("/postulante/form", { state: application });
      }
    }
  }

  const deleteApplicationF = async (id: number) => {
    try {
      const data = await deleteApplication(id);
      console.log(data);
      dispatch(setFetch(!fetch));
    } catch (error) {
      console.log(error);
    }
  };

  const olderThanTwoDays = (date: Date) => {
    if (!date) return;
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - new Date(date).getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 2;
  };

  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [dataToRemove, setDataToRemove] = React.useState({ id: -1, name: "" });

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";
    dispatch(setSortBy(sort));
    dispatch(setPage(0));
    dispatch(setDefaultSort(sortOrder));
  };

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const expectativaCellStyle: React.CSSProperties = {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
    fontFamily: 'ui-monospace, "SF Mono", Consolas, "Liberation Mono", monospace',
    letterSpacing: "0.02em",
  };

  const thStyle: React.CSSProperties = {
    padding: "0.75rem 1rem",
    fontSize: "11px", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.06em",
    color: "#6b7280", background: "#f8fafc",
    borderBottom: "2px solid #e5e7eb",
    cursor: "pointer", whiteSpace: "nowrap",
  };

  const tableWrapperStyle: React.CSSProperties = {
    borderRadius: "16px",
    border: "1px solid #e8edf2",
    backgroundColor: "#ffffff",
    boxShadow: "0 4px 20px rgba(15, 23, 42, 0.08)",
    overflow: "hidden",
  };

  const tableScrollStyle: React.CSSProperties = {
    maxHeight: "500px",
    overflowY: "auto",
    overflowX: "auto",
  };

  return (
    <div className="mb-3" style={{ minHeight: "300px", ...tableWrapperStyle }}>
      <div className="table-responsive" style={tableScrollStyle}>
      <table
        className="table table-panel text-nowrap align-middle mb-0"
        style={{ borderCollapse: "separate", borderSpacing: 0 }}
      >
        <thead>
          <tr style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <th style={thStyle} onClick={() => setSort("id")}>
              Id {sortBy === "id" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("name")}>
              Nombre {sortBy === "name" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("lastName")}>
              Apellido {sortBy === "lastName" && getIconSort()}
            </th>
            <th
              style={{ ...thStyle, textAlign: "center" }}
              onClick={() => setSort("status")}
            >
              Estatus {sortBy === "status" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("applicationDate")}>
              {!user.branch ? "Cita" : "Fecha inicio"}{" "}
              {sortBy === "applicationDate" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("position")}>
              Puesto {sortBy === "position" && getIconSort()}
            </th>
            <th style={thStyle} onClick={() => setSort("shift")}>
              Turno {sortBy === "shift" && getIconSort()}
            </th>
            {can("partners.view_salary", user) && (
              <th style={thStyle} onClick={() => setSort("monthlySalary")}>
                Expectativa {sortBy === "monthlySalary" && getIconSort()}
              </th>
            )}
            <th style={{ ...thStyle, textAlign: "right", cursor: "default" }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {solicitudesList?.length > 0 ? (
            solicitudesList.map((item: any, idx: number) => {
              const statusName = item.applicationStatus.name;
              const badgeStyle = getStatusBadgeStyle(statusName);
              const statusLabel = statusName === "Solicitado" ? "Candidato" : statusName;
              const fecha = `${item.applicationDate} ${item.applicationTime}`;
              const m = moment(fecha);
              const fechaCita = m.format("D MMM YYYY");
              const horaCita = m.format("HH:mm");

              const tdStyle: React.CSSProperties = {
                padding: "0.65rem 1rem",
                fontSize: "13px", color: "#374151",
                borderBottom: "1px solid #f3f4f6",
                verticalAlign: "middle",
              };

              return (
                <tr
                  key={item.id}
                  style={{ background: idx % 2 === 0 ? "#ffffff" : "#fafafa", transition: "background 0.1s" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "#f0f9ff")}
                  onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#fafafa")}
                >
                  <td style={{ ...tdStyle, fontWeight: 600, color: "#6b7280", fontSize: 12 }}>
                    #{item?.id}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>
                    {toCamelCase(item.person.firstName)}
                  </td>
                  <td style={tdStyle}>
                    {toCamelCase(item.person.lastName + " " + item.person.secondLastName)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <span style={badgeStyle}>
                      <i className={`fa-solid ${getStatusIcon(statusName)}`} style={{ fontSize: 9 }} />
                      {statusLabel}
                    </span>
                  </td>
                    {user.branch ? (
                      <td style={tdStyle}>{item?.candidates[0]?.recruitmentDate}</td>
                    ) : (
                      <td style={tdStyle}>
                        <div style={{ lineHeight: 1.3 }}>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{fechaCita}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{horaCita}</div>
                        </div>
                      </td>
                    )}
                    <td style={tdStyle}>{item.vacancy.position.name}</td>
                    <td style={{ ...tdStyle, color: "#6b7280" }}>{item?.shift?.name}</td>
                    {can("partners.view_salary", user) && (
                      <td style={tdStyle}>
                        <span style={expectativaCellStyle}>
                          {getCurrencyFormat(item.monthlySalary)}
                        </span>
                      </td>
                    )}

                    <td>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          alignItems: "center",
                          gap: "0.35rem",
                        }}
                      >
                        {item.applicationStatus.name === "Rechazado" && (
                          <button
                            className="btn btn-azul btn-sm rounded-circle"
                            style={{ width: 32, height: 32, padding: 0 }}
                            type="button"
                            onClick={() => {
                              dispatch(
                                setCurrentUser({
                                  id: item.id,
                                  name: `${item.person.firstName} ${item.person.lastName} ${item.person.secondLastName}`,
                                })
                              );
                              dispatch(setShowModal(true));
                            }}
                          >
                            <i className="fa-solid fa-pencil"></i>
                          </button>
                        )}
                        {item.applicationStatus.name != "Colaborador" &&
                        item.applicationStatus.name != "Rechazado" ? (
                          <button
                            className="btn btn-azul btn-sm rounded-pill"
                            onClick={() => handleClick(item)}
                            type="button"
                            style={{ paddingInline: "1.1rem" }}
                          >
                            Ver detalle
                          </button>
                        ) : null}
                        {item.applicationStatus.name != "Colaborador" && (
                          <button
                            className="btn btn-secondary btn-sm rounded-circle"
                            onClick={() =>
                              navigate(`/solicitudes/tracking/${item.id}`)
                            }
                            type="button"
                            style={{ width: 32, height: 32, padding: 0 }}
                            title="Ver tracking del candidato"
                          >
                            <i className="fa-solid fa-clock-rotate-left"></i>
                          </button>
                        )}

                        {item.applicationStatus.name === "Rechazado" && (
                          <button
                            className="btn btn-rojo btn-sm rounded-circle"
                            type="button"
                            onClick={() => {
                              dispatch(
                                setCurrentUser({
                                  id: item.id,
                                  name: `${item.person.firstName} ${item.person.lastName} ${item.person.secondLastName}`,
                                })
                              );
                              dispatch(setModalBan(true));
                            }}
                            style={{ width: 32, height: 32, padding: 0 }}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                        )}
                        {!(can("files.upload", user) && !can("applications.create_internal", user)) && (
                          <React.Fragment>
                            {!olderThanTwoDays(item.createdAt) && (
                              // <button
                              //   className="btn btn-danger ms-1"
                              //   onClick={() => {
                              //     setShowDeleteModal(true)
                              //     setDataToRemove({
                              //       id: item.id,
                              //       name: `${item.person.firstName} ${item.person.lastName} ${item.person.secondLastName}`,
                              //     })
                              //   }}
                              // >
                              //   <i className="fa-solid fa-ban m"></i>
                              // </button>
                              <></>
                            )}
                            <button
                              className="btn btn-amarillo btn-sm rounded-circle"
                              type="button"
                              onClick={() => {
                                dispatch(
                                  setCurrentUser({
                                    id: item.id,
                                    name: `${item.person.firstName} ${item.person.lastName} ${item.person.secondLastName}`,
                                  })
                                );
                                dispatch(setModalSetStatus(true));
                              }}
                              style={{ width: 32, height: 32, padding: 0 }}
                            >
                              <i className="fa-solid fa-signs-post"></i>
                            </button>
                          </React.Fragment>
                        )}
                        {/* <button
                        className="btn btn-rojo btn-sm"
                        type="button"
                        onClick={() => {
                          console.log(item.id);
                          deleteApplicationF(Number(item.id));
                        }}
                        style={{ marginLeft: "10px" }}
                      >
                        BORRAR
                      </button> */}
                      </div>
                    </td>
                  </tr>
                );
            })
          ) : (
            <tr>
              <td colSpan={9}>
                <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
                  <img src={EmptyImage} alt="" style={{ width: 140, opacity: 0.6, marginBottom: "1rem" }} />
                  <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 0.25rem" }}>
                    Sin solicitudes
                  </p>
                  <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>
                    No se encontraron registros con los filtros aplicados
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      <ModalContainer
        open={showDeleteModal}
        closeFn={() => {
          setShowDeleteModal(false);
        }}
        title={`Eliminar solicitud`}
        children={
          <DeleteSolicitudes
            name={dataToRemove?.name}
            id={dataToRemove?.id}
            closeFn={() => {
              setShowDeleteModal(false);
              setDataToRemove({
                id: -1,
                name: "",
              });
              dispatch(setFetch(!fetch));
            }}
          />
        }
      />
    </div>
  );
};

export default TableSolicitudes;
